import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Menu } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { type CellContext, RowActionsWrapper, useDataTableContext } from "@msuite/ui/table";
import type { Tables } from "@msuite/utils/types";
import React from "react";
import { useNavigate } from "react-router";
import { useCitadel } from "@/hooks/use-citadel";
import { client } from "@/hono";
import { AddServiceModal } from "@/modals/organizations/add-service-modal";
import { EditServiceModal } from "@/modals/organizations/edit-service-modal";

export function RowActions({
	cell,
}: CellContext<Required<Tables<"organizationServicesWithFullNumber">>, "actions">) {
	/** Hooks */
	const { refetch } = useDataTableContext();
	const { t } = useTranslation();
	const editServiceModal = useDisclosure();
	const addServiceModal = useDisclosure();
	const serviceId = cell.row.original.id;
	const asyncModal = useAsyncModal();
	const navigate = useNavigate();
	const { citadel } = useCitadel();

	/** Computed */
	// Opening a service is a read every member of the organisation may perform; changing the
	// catalogue is not. Each entry is offered only where the server would accept it.
	const canCreate = citadel?.can("create", "Service") === true;
	const canUpdate = citadel?.can("update", "Service") === true;
	const canDelete = citadel?.can("delete", "Service") === true;

	/** Functions */
	async function handleOnDelete() {
		try {
			if (!serviceId) return;
			const isConfirmed = await asyncModal.confirm();
			if (!isConfirmed) return;
			await client.organizations.services[":serviceId"].$delete({
				param: { serviceId },
			});
			await refetch?.();
		} catch (error) {
			console.error("Failed to delete service:", error);
		}
	}

	function handleOnNavigateToService() {
		navigate(`/organization/data/services/${serviceId}`);
	}

	/** Render */
	return (
		<React.Fragment>
			<RowActionsWrapper to={`/organization/data/services/${serviceId}`}>
				<Menu
					items={() => [
						{
							label: t("general.open"),
							icon: icon("Info"),
							onClick: handleOnNavigateToService,
							cyId: "row-actions-open-button",
						},
						canCreate
							? {
									label: t("organization.data.add_service"),
									onClick: addServiceModal.onOpen,
									icon: icon("Plus"),
								}
							: null,
						canUpdate
							? {
									label: t("organization.data.edit_service"),
									onClick: editServiceModal.onOpen,
									icon: icon("Edit"),
								}
							: null,
						canDelete ? "divider" : null,
						canDelete
							? {
									label: t("general.delete"),
									onClick: handleOnDelete,
									icon: icon("Trash2"),
									isDanger: true,
								}
							: null,
					]}
				>
					<Button
						size="xxs"
						icon={<Icon name="EllipsisVertical" />}
						variant="ghost"
						data-cy="row-actions-button"
					/>
				</Menu>
			</RowActionsWrapper>
			{editServiceModal.isOpen && (
				<EditServiceModal
					isOpen={editServiceModal.isOpen}
					onClose={editServiceModal.onClose}
					serviceId={serviceId}
					onComplete={async () => {
						await refetch?.();
					}}
				/>
			)}
			{addServiceModal.isOpen && (
				<AddServiceModal
					isOpen={addServiceModal.isOpen}
					onClose={addServiceModal.onClose}
					defaultValues={{ parentServiceId: serviceId || undefined }}
					onComplete={async () => {
						await refetch?.();
					}}
				/>
			)}
			{asyncModal.isOpen && (
				<ConfirmModal
					controller={asyncModal}
					message={t("organization.data.delete_service_confirm")}
				/>
			)}
		</React.Fragment>
	);
}
